import { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function Roles() {
  const { isAdmin } = useAuth();
  const [schema, setSchema] = useState(null);
  const [roles, setRoles] = useState([]);
  const [selected, setSelected] = useState(null);
  const [perms, setPerms] = useState({});
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");

  const load = () => {
    api.get("/roles").then((r) => { setRoles(r.data); if (!selected && r.data[0]) selectRole(r.data[0]); });
  };
  useEffect(() => {
    api.get("/permission-schema").then((r) => setSchema(r.data)).catch(() => {});
    load(); // eslint-disable-next-line
  }, []);

  const selectRole = (role) => {
    setSelected(role);
    const base = {};
    (schema?.modules || Object.keys(role.permissions || {})).forEach((m) => { base[m] = { ...(role.permissions?.[m] || {}) }; });
    setPerms(role.permissions ? JSON.parse(JSON.stringify(role.permissions)) : base);
  };

  if (!isAdmin) return (
    <div className="max-w-md mx-auto mt-16 text-center" data-testid="restricted-notice">
      <Icons.Lock className="h-8 w-8 mx-auto text-destructive mb-3" />
      <h2 className="font-display text-xl font-bold">Administrator Only</h2>
      <p className="text-muted-foreground text-sm mt-2">Only Admins can manage roles &amp; permissions.</p>
    </div>
  );

  const isAdminRole = selected?.key === "admin" || selected?.key === "super_admin";
  const toggle = (m, a) => {
    if (isAdminRole) return;
    setPerms((p) => ({ ...p, [m]: { ...(p[m] || {}), [a]: !(p[m]?.[a]) } }));
  };
  const toggleRow = (m, val) => {
    if (isAdminRole) return;
    const row = {}; (schema.actions).forEach((a) => { row[a] = val; });
    setPerms((p) => ({ ...p, [m]: row }));
  };

  const save = async () => {
    try {
      await api.put(`/roles/${selected.key}`, { name: selected.name, permissions: perms });
      toast.success("Permissions updated — applies to all users in this role");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const createRole = async () => {
    try {
      const { data } = await api.post("/roles", { name: newName });
      toast.success("Role created"); setCreating(false); setNewName("");
      const rs = await api.get("/roles"); setRoles(rs.data);
      selectRole(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteRole = async () => {
    if (!window.confirm(`Delete role "${selected.name}"?`)) return;
    try { await api.delete(`/roles/${selected.key}`); toast.success("Role deleted"); setSelected(null); const rs = await api.get("/roles"); setRoles(rs.data); if (rs.data[0]) selectRole(rs.data[0]); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="overline text-muted-foreground mb-1">Access Control</div>
          <h1 className="font-display text-2xl font-bold tracking-tight">Roles &amp; Permissions</h1>
          <p className="text-sm text-muted-foreground">Turn module permissions on or off per role. Changes apply instantly to all users in that role.</p>
        </div>
        <Button variant="outline" className="gap-1.5" onClick={() => setCreating(true)} data-testid="add-role-btn"><Icons.Plus className="h-4 w-4" />New Role</Button>
      </div>

      <div className="grid lg:grid-cols-[220px_1fr] gap-6">
        {/* Role list */}
        <Card className="p-2 h-fit">
          {roles.map((r) => (
            <button key={r.key} onClick={() => selectRole(r)} data-testid={`role-${r.key}`}
              className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors flex items-center justify-between ${selected?.key === r.key ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}>
              <span className="font-medium">{r.name}</span>
              {r.is_system && <Badge variant="secondary" className="text-[10px]">system</Badge>}
            </button>
          ))}
        </Card>

        {/* Matrix */}
        {selected && schema && (
          <Card className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-display font-semibold text-lg">{selected.name}</h3>
                {isAdminRole && <p className="text-xs text-muted-foreground">Admin always has full access (locked).</p>}
              </div>
              <div className="flex gap-2">
                {!selected.is_system && <Button variant="outline" size="sm" className="text-destructive" onClick={deleteRole} data-testid="delete-role-btn"><Icons.Trash2 className="h-4 w-4 mr-1" />Delete</Button>}
                {!isAdminRole && <Button size="sm" onClick={save} data-testid="save-perms-btn"><Icons.Save className="h-4 w-4 mr-1" />Save Changes</Button>}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 pr-4 overline text-muted-foreground font-semibold">Module</th>
                    {schema.actions.map((a) => <th key={a} className="py-2 px-2 overline text-muted-foreground text-center">{a}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {schema.modules.map((m) => (
                    <tr key={m} className="border-b last:border-0 hover:bg-secondary/40">
                      <td className="py-2 pr-4 font-medium capitalize">
                        <button className="hover:underline text-left" onClick={() => toggleRow(m, !schema.actions.every((a) => perms[m]?.[a]))} disabled={isAdminRole}>
                          {schema.labels[m] || m}
                        </button>
                      </td>
                      {schema.actions.map((a) => (
                        <td key={a} className="py-2 px-2 text-center">
                          <Checkbox checked={isAdminRole ? true : !!perms[m]?.[a]} disabled={isAdminRole}
                            onCheckedChange={() => toggle(m, a)} data-testid={`perm-${m}-${a}`} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Create New Role</DialogTitle></DialogHeader>
          <div className="space-y-1.5"><Label className="overline text-muted-foreground">Role Name</Label>
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Store Manager" data-testid="role-name-input" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
            <Button onClick={createRole} disabled={!newName.trim()} data-testid="create-role-btn">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
